interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  status: number;
}

interface AuthenticationError extends Error {
  name: 'AuthenticationError';
  code: 'MISSING_TOKEN' | 'INVALID_TOKEN' | 'TOKEN_EXPIRED' | 'AUTH_PROVIDER_NOT_SET';
  endpoint: string;
  timestamp: string;
}

class ApiClient {
  private baseUrl: string;
  private isTokenProviderSet = false;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || import.meta.env.VITE_API_BASE_URL || '/api';
  }

  setBaseUrl(baseUrl: string) {
    this.baseUrl = baseUrl;
    console.debug('✅ API Client: Base URL updated to:', baseUrl);
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  private createAuthError(code: AuthenticationError['code'], endpoint: string, message: string): AuthenticationError {
    const error = new Error(message) as AuthenticationError;
    error.name = 'AuthenticationError';
    error.code = code;
    error.endpoint = endpoint;
    error.timestamp = new Date().toISOString();
    return error;
  }

  private logAuthenticationIssue(error: AuthenticationError, additionalContext?: any) {
    console.group(`🔒 Authentication Issue - ${error.code}`);
    console.error('Error:', error.message);
    console.error('Endpoint:', error.endpoint);
    console.error('Timestamp:', error.timestamp);
    console.error('Code:', error.code);
    if (additionalContext) {
      console.error('Additional Context:', additionalContext);
    }
    console.trace('Stack trace:');
    console.groupEnd();

    // Also log to a structured format for monitoring
    const logEntry = {
      type: 'AUTHENTICATION_ERROR',
      code: error.code,
      endpoint: error.endpoint,
      message: error.message,
      timestamp: error.timestamp,
      userAgent: navigator.userAgent,
      url: window.location.href,
      additionalContext
    };

    // Send to console as JSON for easy parsing by log aggregators
    console.warn('AUTH_ERROR_STRUCTURED:', JSON.stringify(logEntry));
  }

  private async getAuthHeaders(endpoint: string): Promise<Record<string, string>> {
    // Check if token provider is set
    if (!this.isTokenProviderSet) {
      const error = this.createAuthError(
        'AUTH_PROVIDER_NOT_SET',
        endpoint,
        'Authentication provider not configured. API client token provider must be set before making authenticated requests.'
      );
      this.logAuthenticationIssue(error, {
        suggestion: 'Ensure AuthProvider is wrapping your app and apiClient.setTokenProvider() is called'
      });
      throw error;
    }

    try {
      console.debug(`🔑 Getting access token for endpoint: ${endpoint}`);
      // Just call getAccessToken - AuthContext handles all auth flow logic
      const accessToken = await this.getAccessToken();

      if (!accessToken) {
        const error = this.createAuthError(
          'MISSING_TOKEN',
          endpoint,
          'No access token available. Authentication may be required.'
        );
        this.logAuthenticationIssue(error, {
          suggestion: 'AuthContext getAccessToken returned null - auth flow should have been triggered'
        });
        throw error;
      }

      console.debug(`✅ Access token obtained for endpoint: ${endpoint}`);
      return {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      };
    } catch (error) {
      // Re-throw authentication errors
      if (error instanceof Error && error.name === 'AuthenticationError') {
        throw error;
      }

      // Handle unexpected errors
      const authError = this.createAuthError(
        'MISSING_TOKEN',
        endpoint,
        `Unexpected error getting authentication token: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
      this.logAuthenticationIssue(authError, { originalError: error });
      throw authError;
    }
  }

  // This will be set by the auth context
  private getAccessToken: () => Promise<string | null> = async () => null;

  setTokenProvider(tokenProvider: () => Promise<string | null>) {
    this.getAccessToken = tokenProvider;
    this.isTokenProviderSet = true;
    console.debug('✅ API Client: Token provider configured');
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount = 0
  ): Promise<ApiResponse<T>> {
    const requestId = Math.random().toString(36).substring(2, 15);
    const startTime = Date.now();

    console.group(`🌐 API Request [${requestId}] ${options.method || 'GET'} ${endpoint}`);
    console.debug('Request options:', { ...options, headers: '***' });

    try {
      // Get authenticated headers - this will throw if auth is missing/invalid
      const headers = await this.getAuthHeaders(endpoint);

      // Use endpoint directly if it's a full URL, otherwise prepend base URL
      const fullUrl = endpoint.startsWith('https://') || endpoint.startsWith('http://')
        ? endpoint
        : `${this.baseUrl}${endpoint}`;
      console.debug('Making request to:', fullUrl);
      console.debug('Headers:', { ...headers, Authorization: headers.Authorization ? 'Bearer ***' : 'Not set' });

      const response = await fetch(fullUrl, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
      });

      const responseTime = Date.now() - startTime;
      console.debug(`Response received in ${responseTime}ms:`, {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      let data = null;
      try {
        data = await response.json();
        console.debug('Parsed JSON response:', data);
      } catch (jsonError) {
        console.debug('Response is not JSON:', jsonError);
      }

      // Handle 401 Unauthorized with detailed logging
      if (response.status === 401) {
        console.group('🚫 401 Unauthorized Response');
        console.error('Authentication failed for request');
        console.error('Response data:', data);
        console.error('Request headers had Authorization:', !!headers.Authorization);

        // Log additional context for debugging
        const authContext = {
          endpoint,
          requestId,
          responseTime,
          hasAuthHeader: !!headers.Authorization,
          responseData: data,
          retryCount
        };

        console.error('AUTH_FAILURE_STRUCTURED:', JSON.stringify({
          type: 'API_AUTH_FAILURE',
          ...authContext,
          timestamp: new Date().toISOString(),
          url: window.location.href
        }));
        console.groupEnd();

        // Emit a custom event that components can listen to for handling 401s
        window.dispatchEvent(new CustomEvent('api-auth-failure', {
          detail: {
            endpoint,
            responseData: data,
            timestamp: new Date().toISOString()
          }
        }));

        // Don't retry on 401 - authentication issue needs to be resolved
        console.groupEnd();
        return {
          error: data?.message || 'Your session has expired. Please sign in again.',
          status: response.status,
        };
      }

      // Handle other HTTP errors
      if (!response.ok) {
        console.warn(`HTTP ${response.status} error:`, data);
        console.groupEnd();
        return {
          error: data?.message || `HTTP ${response.status}: ${response.statusText}`,
          status: response.status,
        };
      }

      console.debug('✅ Request successful');
      console.groupEnd();
      return {
        data,
        status: response.status,
      };

    } catch (error) {
      const responseTime = Date.now() - startTime;

      // Handle authentication errors specifically
      if (error instanceof Error && error.name === 'AuthenticationError') {
        console.error('❌ Authentication error prevented request');
        console.groupEnd();
        return {
          error: error.message,
          status: 401, // Treat auth errors as 401
        };
      }

      // Handle network/other errors
      console.error(`❌ Request failed after ${responseTime}ms:`, error);

      const errorContext = {
        endpoint,
        requestId,
        responseTime,
        error: error instanceof Error ? error.message : 'Unknown error',
        retryCount
      };

      console.error('REQUEST_ERROR_STRUCTURED:', JSON.stringify({
        type: 'API_REQUEST_ERROR',
        ...errorContext,
        timestamp: new Date().toISOString(),
        url: window.location.href
      }));

      console.groupEnd();
      return {
        error: error instanceof Error ? error.message : 'Network error',
        status: 0,
      };
    }
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * Check if the client is properly configured for authenticated requests
   * Just checks if we can get a token - AuthContext handles validation
   */
  async checkAuthenticationStatus(): Promise<{
    isConfigured: boolean;
    hasToken: boolean;
    tokenValid: boolean;
    error?: string;
  }> {
    console.group('🔍 Authentication Status Check');

    try {
      if (!this.isTokenProviderSet) {
        console.warn('Token provider not configured');
        console.groupEnd();
        return {
          isConfigured: false,
          hasToken: false,
          tokenValid: false,
          error: 'Token provider not set'
        };
      }

      // Just try to get a token - AuthContext handles all validation and renewal
      const token = await this.getAccessToken();

      if (!token) {
        console.warn('No access token available from AuthContext');
        console.groupEnd();
        return {
          isConfigured: true,
          hasToken: false,
          tokenValid: false,
          error: 'No access token'
        };
      }

      console.debug('✅ Authentication status: Token available from AuthContext');
      console.groupEnd();
      return {
        isConfigured: true,
        hasToken: true,
        tokenValid: true // Trust AuthContext validation
      };
    } catch (error) {
      console.error('Error checking authentication status:', error);
      console.groupEnd();
      return {
        isConfigured: this.isTokenProviderSet,
        hasToken: false,
        tokenValid: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Make a request and automatically handle authentication errors with user-friendly messages
   */
  async makeAuthenticatedRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T> & { authError?: AuthenticationError }> {
    try {
      return await this.makeRequest<T>(endpoint, options);
    } catch (error) {
      if (error instanceof Error && error.name === 'AuthenticationError') {
        return {
          error: this.getAuthErrorUserMessage(error as AuthenticationError),
          status: 401,
          authError: error as AuthenticationError
        };
      }
      throw error;
    }
  }

  private getAuthErrorUserMessage(error: AuthenticationError): string {
    switch (error.code) {
      case 'AUTH_PROVIDER_NOT_SET':
        return 'Application configuration error. Please refresh the page and try again.';
      case 'MISSING_TOKEN':
        return 'You need to sign in to access this feature.';
      case 'INVALID_TOKEN':
        return 'Your session is invalid. Please sign in again.';
      case 'TOKEN_EXPIRED':
        return 'Your session has expired. Please sign in again.';
      default:
        return 'Authentication error. Please sign in again.';
    }
  }
}

export const apiClient = new ApiClient();
export default apiClient;